import { CLOSED_AASM_STATES } from 'stash/constants/mappings';
import {
	CUSTODIAN,
	PERSONAL_BROKERAGE,
	ROBO_PERSONAL_BROKERAGE,
	ROTH_IRA,
	TRADITIONAL_IRA,
} from 'stash/constants/accountTypes';
import { addMonths, addWeeks, getDateString } from 'stash/utils/time';

type AutostashFrequency = 'MONTHLY' | 'BI-WEEKLY' | 'WEEKLY';

export type Contribution = {
	year: number;
	contribution_limit: number;
	raw_pending_value: number;
	raw_completed_value: number;
};

type AccountWithState = {
	state: string;
};

type OrderableAccount = {
	type?: string;
};

type JwtPayload = {
	sub?: string;
	exp?: number;
	[key: string]: unknown;
};

export const isEmpty = (obj?: object | null): boolean => {
	return !obj || !Object.keys(obj).length;
};

export const getAutostashNextExecution = (frequency?: AutostashFrequency): string => {
	switch (frequency) {
		case 'MONTHLY':
			return getDateString(addMonths(new Date(), 1));
		case 'BI-WEEKLY':
			return getDateString(addWeeks(new Date(), 2));
		case 'WEEKLY':
		default:
			return getDateString(addWeeks(new Date(), 1));
	}
};

// Returns the maximum transaction amount for an account with limits (retire)
export const transactionMax = (
	currentContributions?: Contribution[],
	year?: string,
): number | undefined => {
	if (currentContributions && currentContributions.length) {
		const currentYear =
			currentContributions.find((c) => c.year === parseInt(year ?? '', 10)) ||
			currentContributions[0];

		return (
			currentYear.contribution_limit -
			currentYear.raw_pending_value -
			currentYear.raw_completed_value
		);
	}
};

export const isRetireAccount = (accountType?: string): boolean => {
	return ([TRADITIONAL_IRA, ROTH_IRA] as string[]).includes(accountType ?? '');
};

export const hasClosedAllAccounts = (accounts: AccountWithState[]): boolean => {
	return (
		!!accounts.length &&
		accounts.every((a) => (CLOSED_AASM_STATES as Record<string, boolean>)[a.state])
	);
};

export const getOrderedAccounts = (
	accounts: OrderableAccount[] | Record<string, OrderableAccount> = [],
): OrderableAccount[] => {
	// normalize accounts into an array
	const accountArray = Array.isArray(accounts) ? accounts : Object.values(accounts);

	return accountArray.sort((a, b) => {
		// always return personal brokerage first
		if (a.type === PERSONAL_BROKERAGE) return -1;
		if (b.type === PERSONAL_BROKERAGE) return 1;

		// return smart portfolio directly after personal brokerage
		if (a.type === ROBO_PERSONAL_BROKERAGE) return -1;
		if (b.type === ROBO_PERSONAL_BROKERAGE) return 1;

		// custodian accounts are always last
		if (a.type === CUSTODIAN) return 1;
		if (b.type === CUSTODIAN) return -1;

		// sort all IRA accounts in between the others
		return 0;
	});
};

export const parseJwt = (token: string): JwtPayload | null => {
	try {
		return JSON.parse(atob(token.split('.')[1]));
	} catch (_e) {
		return null;
	}
};

export const isCancelAccountPending = (action?: string): boolean => {
	return action === 'cancel';
};

export const diffArray = (array1: string[], array2: string[]): string[] => {
	return array1.filter((x) => !array2.includes(x));
};
